<template>
  <template
    v-if="
      page_module &&
        page_module?.reference_code &&
        isModuleActivated(page_module.reference_code) &&
        isModuleReady(page_module.reference_code)
    ">
    <main class="flex flex-col w-full h-[calc(100vh-80px)] overflow-y-hidden focus:outline-none">
      <slot name="header">
        <PageBreadCrumb
          :page-reference="page_reference"
          :current-tab="currentTab"
          :selected-journey="selectedJourney"
          :all-journeys="allJourneys"
          :is-page-title-hidden="$store.state.ui.side_menu_open ? true : isPageTitleHidden"
          :page-title="pageTitle"
          :page-bread-crumb="pageBreadCrumb">
          <template #right_menu>
            <Menu v-if="top_right_menu_actions.length" as="div" class="relative flex-shrink-0 ml-auto">
              <MenuButton
                class="w-9 h-9 border-[1px] bg-white shadow-none dark:bg-dark-screen-background inline-flex items-center justify-center text-gray-400 rounded border-light-divider-border dark:border-dark-divider-border transition ease-in-out duration-side-menu-transition-duration">
                <Pencil :color="$store.state.ui.darkMode ? '#fff' : '#899399'" class="p-1 border-none h-7 w-7" />
              </MenuButton>
              <transition
                enter-active-class="transition duration-100 ease-out"
                enter-from-class="transform scale-95 opacity-0"
                enter-to-class="transform scale-100 opacity-100"
                leave-active-class="transition duration-75 ease-in"
                leave-from-class="transform scale-100 opacity-100"
                leave-to-class="transform scale-95 opacity-0">
                <MenuItems
                  as="ul"
                  class="absolute right-0 z-50 origin-top-right bg-white border rounded-lg shadow-lg w-fit border-light-divider-border dark:border-dark-divider-border dark:bg-dark-screen-background">
                  <MenuItem v-for="item in top_right_menu_actions" :key="item.name" v-slot="{ active }" as="li">
                    <div v-if="item?.divider" class="divide-y divide-gray-100" />
                    <button
                      v-else
                      :disabled="item.disabled"
                      :class="[
                        active
                          ? 'bg-light-brand-blue dark:bg-dark-brand-blue text-dark-body-text'
                          : 'text-light-body-text dark:text-dark-body-text',
                        'w-full whitespace-nowrap group sm:text-sm flex items-center px-4 py-2  disabled:dark:bg-dark-transparent-brand-blue disabled:cursor-default disabled:opacity-60 transition ease-in-out duration-side-menu-transition-duration'
                      ]"
                      @click="item.action">
                      <component
                        :is="item.icon"
                        v-if="item.icon"
                        :class="active ? 'text-dark-body-text' : 'text-common-grey'"
                        class="w-5 h-5 mr-3 transition ease-in-out duration-side-menu-transition-duration"
                        aria-hidden="true" />
                      {{ item.name }}
                    </button>
                  </MenuItem>
                </MenuItems>
              </transition>
            </Menu>
          </template>
        </PageBreadCrumb>
      </slot>

      <slot name="body" />
      <slot name="footer" />
    </main>
  </template>
  <template v-else-if="isModuleActivated(page_module.reference_code) && !isModuleReady(page_module.reference_code)">
    <div class="relative w-full h-full">
      <img
        class="absolute top-0 left-0 object-cover w-full h-full"
        :src="`/src/assets/${page_reference}_${$store.state.ui.darkMode ? 'dark' : 'light'}.jpg`"
        alt="">
      <div class="absolute inset-0 flex flex-col items-center -mt-16 top-1/2 dark:text-white">
        <p class="mb-2 text-2xl text-gray-600 dark:text-white">
          {{ $t("Welcome to the") }}
          <span class="font-bold capitalize">{{ page_module.name }}</span>
          {{ $t("module") }}!
        </p>
        <p class="mb-2 text-xl text-gray-600 dark:text-white">
          {{ module_setup_description_msg }}
        </p>
        <div v-if="module_settings_url" class="primary-btn w-fit" @click="$router.push(module_settings_url)">
          <Settings class="ml-1 w-7 h-7" />
          <div class="pl-1">
            <span>{{ $t("setup") }} {{ page_module.name }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>
  <template v-else>
    <div class="relative w-full h-full">
      <img
        class="absolute top-0 left-0 object-cover w-full h-full"
        :src="`/src/assets/${page_reference}_${$store.state.ui.darkMode ? 'dark' : 'light'}.jpg`"
        alt="">
      <div class="absolute inset-0 flex flex-col items-center -mt-16 top-1/2 dark:text-white">
        <p class="mb-2 text-2xl text-gray-600 dark:text-white">
          {{ $t("Want access to the") }}
          <span class="font-bold capitalize">{{ page_module.name }}</span>
          {{ $t("module") }}?
        </p>
        <p class="mb-2 text-xl text-gray-600 dark:text-white">
          {{ module_activate_description_msg }}
        </p>
        <div v-if="module_activation_url" class="primary-btn w-fit" @click="$router.push(module_activation_url)">
          <LockOpen class="w-5 h-5 mr-2 border-none dark:text-white" />
          <div class="pl-1">
            <span>{{ $t("unlock") }}</span>
          </div>
        </div>
      </div>
    </div>
  </template>
</template>

<script>
import ObjectsView from "@/components/shared/object-view/ObjectsView.vue"
import PageBreadCrumb from "@/components/shared/pages/PageBreadCrumb.vue"
import RollUpMetricsGrid from "@/components/shared/rollup-metrics/RollUpMetricsGrid.vue"
import { PAGE_REFERENCES, PAGE_REFERENCES_MODULES, VIEW_TYPES } from "@/store/constants/references"
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue"
import { Plus, Trash2, ChartColumnIncreasing, Pencil, LockOpen, Settings, Monitor, ChartNoAxesColumnDecreasing } from "lucide-vue-next"
import { markRaw } from "vue"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"

export default {
  components: {
    Settings,
    ChartColumnIncreasing,
    Monitor,
    ChartNoAxesColumnDecreasing: markRaw(ChartNoAxesColumnDecreasing),
    ObjectsView,
    PageBreadCrumb,
    LockOpen,
    Pencil,
    Menu,
    MenuButton,
    MenuItem,
    MenuItems,
    RollUpMetricsGrid,
    Plus,
    Trash2
  },
  inject: ["isModuleActivated", "isModuleReady", "getModule"],
  props: {
    pageReference: {
      type: String,
      required: true
    },
    currentTab: {
      type: String,
      required: false
    },
    currentObjectType: {
      type: Object,
      required: false
    },
    currentForecastName: {
      type: String,
      required: false
    },
    pageTopRightMenuActions: {
      type: Array,
      default: null,
      required: false
    },
    pageBreadCrumb: {
      type: Array,
      default: null,
      required: false
    },
    selectedJourney: {
      type: Object,
      required: false
    },
    allJourneys: {
      type: Array,
      required: false
    },
    isPageTitleHidden: {
      type: Boolean,
      default: false
    },
    pageTitle: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      forecasting_top_right_menu_extra_actions: [
        {
          name: this.$t("Show/Hide Table Metrics"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.UPDATE_FORECAST_TABLE_METRICS)
          },
          disabled: false,
          icon: ChartNoAxesColumnDecreasing
        }
      ],
      default_top_right_menu_actions: [
        {
          name: this.$t("Add/Remove Rollup Metrics"),
          action: () => {
            const metrics_obj = {
              page_reference: this.pageReference,
              object_type: this.currentObjectType?.type,
              view_type: VIEW_TYPES.ROLLUPS,
              current_forecast_name: this.currentForecastName
            }
            if (this.$route.params?.page) {
              metrics_obj["layout_page_reference"] = this.$route.params?.page
            }

            this.$emitter.emit(ALL_EMITTERS.UPDATE_ROLLUP_METRICS, metrics_obj)
          },
          disabled: false,
          icon: ChartNoAxesColumnDecreasing
        },
        {
          name: this.$t("Create a Graph"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_GRAPH, {
              page_reference: this.pageReference,
              object_type: this.currentObjectType?.type,
              filter: ""
            })
          },
          disabled: false,
          icon: ChartColumnIncreasing
        },
        {
          name: this.$t("Show/Hide Graphs"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.SHOW_HIDE_GRAPHS, {
              page_reference: this.pageReference,
              object_type: this.currentObjectType?.type
            })
          },
          disabled: false,
          icon: Monitor
        }
      ],
      journeys_top_right_menu_actions: [
        {
          name: this.$t("Create New Phase"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.CREATE_NEW_PHASE, {
              journeys: this.allJourneys,
              selected_journey: this.selectedJourney
            })
          },
          disabled: false,
          icon: Plus
        },
        {
          name: this.$t("Edit Journey"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.EDIT_JOURNEY, {
              journey: this.selectedJourney
            })
          },
          disabled: false,
          icon: Pencil
        },
        {
          name: this.$t("Delete Journey"),
          action: () => {
            this.$emitter.emit(ALL_EMITTERS.DELETE_JOURNEY, {
              journey: this.selectedJourney
            })
          },
          disabled: false,
          icon: Trash2
        }
      ]
    }
  },
  computed: {
    page_reference() {
      return this.pageReference
    },
    page_module() {
      return this.getModule(PAGE_REFERENCES_MODULES[this.pageReference])
    },
    module_setup_description_msg() {
      return this.$t(`setup_${this.pageReference}_desc`)
    },
    module_activate_description_msg() {
      return this.$t(`activate_${this.pageReference}_desc`)
    },
    module_activation_url() {
      switch (this.pageReference) {
        case PAGE_REFERENCES.PORTFOLIO:
        case PAGE_REFERENCES.PROSPECTING:
          return "/admin/integrations/"
        case PAGE_REFERENCES.FORECASTING:
          return "/organization/?tab=forecasting-preferences"
        default:
          return "/"
      }
    },
    module_settings_url() {
      switch (this.pageReference) {
        case PAGE_REFERENCES.PROSPECTING_GROWTH_MATRIX:
          return "/organization/?tab=growth"
        case PAGE_REFERENCES.PROSPECTING_SUGGESTED:
          return "/organization/?tab=suggested"
        case PAGE_REFERENCES.FORECASTING:
          return "/organization/?tab=forecasting-preferences"
        default:
          return null
      }
    },
    top_right_menu_actions() {
      if (Array.isArray(this.pageTopRightMenuActions)) {
        return this.pageTopRightMenuActions
      } else {
        switch (this.page_reference) {
          case PAGE_REFERENCES.PORTFOLIO:
          case PAGE_REFERENCES.PROSPECTING:
          case PAGE_REFERENCES.PROSPECTING_PROSPECT:
          case PAGE_REFERENCES.PROSPECTING_SUGGESTED:
          case PAGE_REFERENCES.PROSPECTING_GROWTH_MATRIX:
          case PAGE_REFERENCES.PROSPECTING_EXPANSION_LISTS:
          case PAGE_REFERENCES.DASHBOARD:
            return this.default_top_right_menu_actions
          case PAGE_REFERENCES.OBJECT_PROFILE:
          case PAGE_REFERENCES.COMPANY_PROFILE:
            return []
          case PAGE_REFERENCES.FORECASTING:
            return this.default_top_right_menu_actions.concat(this.forecasting_top_right_menu_extra_actions)
          case PAGE_REFERENCES.CUSTOMER_JOURNEYS:
            return this.journeys_top_right_menu_actions
          default:
            return []
        }
      }
    }
  }
}
</script>
